import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/cn';
import { fmtInt, fmtUsd, normalizeFormat } from '@/lib/format';
import type { Wine } from './types';

export interface WineGroup {
  key: string;
  name: string;
  winery_name: string | null;
  format: string;
  grape_summary: string | null;
  years: {
    vintage: number | null;
    stock: number;
    wine_id: number;
    is_keeper: boolean;
    estimated_value_usd: number | null;
  }[];
  total_stock: number;
  estimated_value_total_usd: number;
}

/**
 * Agrupa una lista de wines por (winery_id, name, format) para mostrar en
 * el listado mobile. Cada grupo es "el vino genérico" (sin importar la añada).
 */
export function groupWines(wines: Wine[]): WineGroup[] {
  const map = new Map<string, WineGroup>();

  for (const w of wines) {
    // Firma de cepas ordenada por id para que Malbec+Cabernet == Cabernet+Malbec.
    // Vinos sin cepas cargadas se agrupan entre sí (string vacío).
    const grapeIds = (w.grape_varieties ?? [])
      .map((g) => g.id)
      .sort((a, b) => a - b)
      .join(',');
    const key = `${w.winery?.id ?? 0}|${w.name.trim().toLowerCase()}|${normalizeFormat(w.format)}|${grapeIds}`;
    let group = map.get(key);
    if (!group) {
      // Resumen legible de cepas: nombres separados por " · ". "A Lisa Malbec" y
      // "A Lisa Semillón" quedan como grupos distintos con hint visual.
      const grapeSummary = (w.grape_varieties ?? [])
        .map((g) => g.name)
        .join(' · ') || null;
      group = {
        key,
        name: w.name,
        winery_name: w.winery?.name ?? null,
        format: w.format,
        grape_summary: grapeSummary,
        years: [],
        total_stock: 0,
        estimated_value_total_usd: 0,
      };
      map.set(key, group);
    }
    const stock = w.current_stock ?? 0;
    group.years.push({
      vintage: w.vintage,
      stock,
      wine_id: w.id,
      is_keeper: w.is_keeper,
      estimated_value_usd: w.estimated_value_usd ?? null,
    });
    group.total_stock += stock;
    if (w.estimated_value_usd) {
      group.estimated_value_total_usd += w.estimated_value_usd * stock;
    }
  }

  // Ordenar años por vintage ascendente; los sin añada van al final.
  for (const g of map.values()) {
    g.years.sort((a, b) => {
      if (a.vintage == null) return 1;
      if (b.vintage == null) return -1;
      return a.vintage - b.vintage;
    });
  }

  // Devolver grupos ordenados por nombre.
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
  );
}

export function GroupedWineCard({ group }: { group: WineGroup }) {
  const [expanded, setExpanded] = useState(false);
  const hasMultiple = group.years.length > 1;

  return (
    <li className="card p-3">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-notion-800 leading-tight">
              {group.name}{' '}
              <span className="text-notion-500 text-xs font-normal">· {group.format}</span>
            </p>
            <p className="text-xs text-notion-500 truncate">
              {group.winery_name ?? '—'}
              {group.grape_summary && (
                <span className="text-notion-400"> · {group.grape_summary}</span>
              )}
            </p>
          </div>
          {hasMultiple ? (
            expanded ? (
              <ChevronDown size={16} className="text-notion-400 shrink-0 mt-0.5" />
            ) : (
              <ChevronRight size={16} className="text-notion-400 shrink-0 mt-0.5" />
            )
          ) : null}
        </div>

        {/* Mini timeline: stamps por año presente con cantidad */}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {group.years.map((y, i) => (
            <YearStamp
              key={`${y.vintage ?? 'sin'}-${i}`}
              vintage={y.vintage}
              stock={y.stock}
            />
          ))}
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px] text-notion-500">
          <span>
            <strong className="text-notion-800">{fmtInt(group.total_stock)}</strong>{' '}
            {group.total_stock === 1 ? 'botella' : 'botellas'}
          </span>
          {group.estimated_value_total_usd > 0 && (
            <span>
              Mercado:{' '}
              <strong className="text-notion-800">
                {fmtUsd(group.estimated_value_total_usd)}
              </strong>
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <ul className="mt-3 pt-3 border-t border-notion-100 space-y-1">
          {group.years.map((y) => (
            <li key={y.wine_id}>
              <Link
                to={`/wines/${y.wine_id}`}
                className="flex items-center justify-between gap-2 px-2 py-2 -mx-2 rounded hover:bg-notion-50 active:bg-notion-100"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={cn(
                      'text-xs font-medium tabular-nums w-12 text-center px-1 py-0.5 rounded border',
                      y.is_keeper
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800',
                    )}
                  >
                    {y.vintage ?? 's/a'}
                  </span>
                  <span className="text-sm text-notion-800">
                    {fmtInt(y.stock)} {y.stock === 1 ? 'botella' : 'botellas'}
                  </span>
                </span>
                <span className="text-[11px] text-notion-500">
                  {y.estimated_value_usd != null ? fmtUsd(y.estimated_value_usd) : '—'}{' '}
                  <ChevronRight size={12} className="inline -mt-0.5 text-notion-400" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function YearStamp({ vintage, stock }: { vintage: number | null; stock: number }) {
  return (
    <div className="flex flex-col items-center px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 min-w-[44px]">
      <span className="text-[11px] font-medium text-emerald-800 tabular-nums leading-none">
        {vintage ?? 's/a'}
      </span>
      <span className="text-[10px] text-emerald-700 tabular-nums leading-tight mt-0.5">
        {fmtInt(stock)}
      </span>
    </div>
  );
}
